"use client";

import { useMemo, useState } from "react";
import {
  CopyButton,
  ErrorNotice,
  Notice,
  PillGroup,
  SelectField,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import { WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import { useToolLocale, useToolUi } from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  computeDivision,
  type DivisionComputation,
  type DivisionView,
  endingSentence,
  MAX_REPEAT_SHOWN,
  stepSentences,
} from "@/lib/tool-kit/arithmetic/division-view";
import {
  type DecimalExpansion,
  roundedQuotient,
} from "@/lib/tool-kit/arithmetic/expansion";
import {
  type DivisionLayout,
  type DivisionMode,
  layoutDivision,
  quotientText,
  scaledDividendText,
} from "@/lib/tool-kit/arithmetic/long-division";
import { gcdBig } from "@/lib/tool-kit/math/bigint";

/** English strings. A translated content file overrides these through `ui`. */
export const LONG_DIVISION_CALCULATOR_STRINGS = {
  dividendLabel: "Dividend (the number being divided)",
  divisorLabel: "Divisor (the number you divide by)",
  modeLabel: "Give the answer as",
  modeRemainder: "Quotient and remainder",
  modeDecimal: "Decimal",
  placesLabel: "Decimal places to work out",
  places: "one: {count} place|other: {count} places",
  layoutLabel: "Layout",
  layoutBracket: "Bracket (English-speaking countries, “bus stop”)",
  layoutInline: "Inline (Germany, Austria, Switzerland)",
  results: "Results",
  quotient: "Quotient",
  remainder: "Remainder",
  decimal: "Decimal",
  roundedTo: "Rounded to {places}",
  mixedNumber: "Mixed number",
  fraction: "As a fraction",
  repeatLength: "Length of the repeating block",
  digits: "one: {count} digit|other: {count} digits",
  check: "Check",
  checkLine: "{quotient} × {divisor} + {remainder} = {dividend}",
  diagram: "Long division layout",
  diagramLabel: "Long division of {dividend} by {divisor}, quotient {quotient}",
  stepsHeading: "Step by step",
  moveDecimal:
    "Move the decimal point {count} to the right in both numbers so the divisor is a whole number: {dividend} ÷ {divisor} becomes {scaledDividend} ÷ {scaledDivisor}.",
  signs:
    "Signs are set aside while dividing and put back at the end: the answer is negative when exactly one number is negative.",
  start:
    "Start from the left with the first digits that make a number {divisor} can go into: {current}.",
  bringDown: "Bring down the {digit}.",
  appendZero:
    "Add a zero at the end of the dividend and bring it down: the remainder becomes {current}.",
  goesIn:
    "{divisor} goes into {current} {times}: {divisor} × {quotient} = {product}. Write {quotient} above the line, then subtract: {current} − {product} = {remainder}.",
  times: "one: {count} time|other: {count} times",
  zeroTimes:
    "{divisor} does not go into {current}, so write 0 above the line and keep going.",
  endExact: "The remainder is 0, so the division is exact.",
  endRemainder:
    "No digits are left to bring down, so the remainder is {remainder}.",
  endRepeat:
    "The remainder {remainder} has appeared before, so the digits {block} repeat forever.",
  endPlaces:
    "Stopped after {places}. The division does not end: the exact decimal is {exact}.",
  stepsCapped: "Showing the first {count} steps.",
  remainderShort: "R",
  fellBack:
    "Remainder form needs whole numbers, so this is shown as a decimal instead.",
  negativeRemainder:
    "With a negative dividend the remainder takes the dividend’s sign (the “truncated” convention). Other conventions give a different remainder — see the Modulo Calculator.",
  enterBoth: "Enter a dividend and a divisor to see the working.",
  invalid: "Enter plain numbers such as 1234, 7.5 or −42.",
  divideByZero: "You cannot divide by zero — there is no number that works.",
  tooLong: "Use numbers of at most 40 digits each.",
  copy: "Copy answer",
  copied: "Copied!",
  copyText: "{dividend} ÷ {divisor} = {answer}",
} as const;

type Strings = typeof LONG_DIVISION_CALCULATOR_STRINGS;

const PLACE_CHOICES = [1, 2, 3, 4, 5, 6, 8, 10, 12, 15, 20, 25, 30];
const PRE_CLASS =
  "overflow-x-auto rounded-lg border border-border bg-muted/30 p-4 font-mono text-foreground text-sm leading-snug";
const LIST_CLASS = "list-decimal space-y-2 pl-6 text-foreground text-sm";

function signed(value: bigint): string {
  return value < BigInt(0) ? `(−${(-value).toString()})` : value.toString();
}

function plainSigned(value: bigint): string {
  return value < BigInt(0) ? `−${(-value).toString()}` : value.toString();
}

function RepeatingDecimal({ expansion }: { expansion: DecimalExpansion }) {
  const sign = expansion.negative ? "−" : "";
  if (expansion.repeating === "") {
    const tail =
      expansion.nonRepeating === "" ? "" : `.${expansion.nonRepeating}`;
    return (
      <>
        {sign}
        {expansion.integer}
        {tail}
        {expansion.complete ? "" : "…"}
      </>
    );
  }
  const shown = expansion.repeating.slice(0, MAX_REPEAT_SHOWN);
  const more = expansion.repeating.length > MAX_REPEAT_SHOWN ? "…" : "";
  return (
    <>
      {sign}
      {expansion.integer}.{expansion.nonRepeating}
      <span className="overline decoration-2">{shown}</span>
      {more}
    </>
  );
}

function RemainderStats({
  view,
  strings,
}: {
  view: DivisionView;
  strings: Strings;
}) {
  const { division, fraction } = view;
  const signedRemainder = view.dividendText.startsWith("−")
    ? -division.remainder
    : division.remainder;
  const whole = BigInt(division.quotientInteger);
  const divisor = division.divisor;
  const rest = gcdBig(division.remainder, divisor);
  const mixed =
    division.remainder === BigInt(0)
      ? null
      : `${division.negative ? "−" : ""}${whole.toString()} ${(division.remainder / rest).toString()}/${(divisor / rest).toString()}`;
  const quotient = division.negative ? -whole : whole;
  const divisorSigned = view.divisorText.startsWith("−") ? -divisor : divisor;
  const checkTotal = quotient * divisorSigned + signedRemainder;
  return (
    <>
      <StatGrid>
        <StatCard
          emphasis
          label={strings.quotient}
          value={quotientText(division)}
        />
        <StatCard
          label={strings.remainder}
          value={plainSigned(signedRemainder)}
        />
        {mixed ? <StatCard label={strings.mixedNumber} value={mixed} /> : null}
        <StatCard
          label={strings.fraction}
          value={`${plainSigned(fraction.num)}${fraction.den === BigInt(1) ? "" : `/${fraction.den.toString()}`}`}
        />
      </StatGrid>
      <p className="font-mono text-muted-foreground text-sm">
        {strings.check}:{" "}
        {t(strings.checkLine, {
          quotient: signed(quotient),
          divisor: signed(divisorSigned),
          remainder: signed(signedRemainder),
          dividend: plainSigned(checkTotal),
        })}
      </p>
      {signedRemainder < BigInt(0) ? (
        <Notice>{strings.negativeRemainder}</Notice>
      ) : null}
    </>
  );
}

function DecimalStats({
  view,
  strings,
  places,
  locale,
}: {
  view: DivisionView;
  strings: Strings;
  places: number;
  locale: string;
}) {
  const { expansion, fraction } = view;
  const needsRounding =
    expansion.repeating !== "" ||
    !expansion.complete ||
    expansion.nonRepeating.length > places;
  const rounded = needsRounding
    ? roundedQuotient(fraction.num, fraction.den, places)
    : null;
  return (
    <StatGrid>
      <StatCard
        emphasis
        label={strings.decimal}
        value={<RepeatingDecimal expansion={expansion} />}
      />
      {rounded ? (
        <StatCard
          label={t(strings.roundedTo, {
            places: tPlural(strings.places, locale, places).trim(),
          })}
          value={rounded}
        />
      ) : null}
      {expansion.repeating === "" ? null : (
        <StatCard
          label={strings.repeatLength}
          value={tPlural(
            strings.digits,
            locale,
            expansion.repeating.length
          ).trim()}
        />
      )}
      <StatCard
        label={strings.fraction}
        value={`${plainSigned(fraction.num)}${fraction.den === BigInt(1) ? "" : `/${fraction.den.toString()}`}`}
      />
    </StatGrid>
  );
}

function Working({
  view,
  layout,
  strings,
  locale,
}: {
  view: DivisionView;
  layout: DivisionLayout;
  strings: Strings;
  locale: string;
}) {
  const { division } = view;
  const lines = layoutDivision(division, layout);
  const steps = stepSentences(division, strings, locale);
  return (
    <>
      <h3 className={WIDGET_H3}>{strings.diagram}</h3>
      <pre
        aria-label={t(strings.diagramLabel, {
          dividend: view.dividendText,
          divisor: view.divisorText,
          quotient: view.answerText,
        })}
        className={PRE_CLASS}
        role="img"
      >
        {lines.join("\n")}
      </pre>
      <h3 className={WIDGET_H3}>{strings.stepsHeading}</h3>
      {division.shift > 0 ? (
        <p className="text-foreground text-sm">
          {t(strings.moveDecimal, {
            count: tPlural(strings.places, locale, division.shift).trim(),
            dividend: view.dividendText.replace("−", ""),
            divisor: view.divisorText.replace("−", ""),
            scaledDividend: scaledDividendText(division),
            scaledDivisor: division.divisor.toString(),
          })}
        </p>
      ) : null}
      {view.dividendText.startsWith("−") || view.divisorText.startsWith("−") ? (
        <p className="text-muted-foreground text-sm">{strings.signs}</p>
      ) : null}
      <ol className={LIST_CLASS}>
        {steps.lines.map((line, index) => (
          // Steps are positional: the same sentence can recur.
          // biome-ignore lint/suspicious/noArrayIndexKey: positional steps
          <li key={index}>{line}</li>
        ))}
      </ol>
      {steps.capped ? (
        <p className="text-muted-foreground text-sm">
          {t(strings.stepsCapped, { count: steps.lines.length })}
        </p>
      ) : (
        <p className="text-foreground text-sm">
          {endingSentence(division, view.expansion, strings, locale)}
        </p>
      )}
    </>
  );
}

export default function LongDivisionCalculatorWidget() {
  const strings = useToolUi(LONG_DIVISION_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const [dividend, setDividend] = useState("1234");
  const [divisor, setDivisor] = useState("56");
  const [mode, setMode] = useState<DivisionMode>("remainder");
  const [places, setPlaces] = useState("6");
  const [layout, setLayout] = useState<DivisionLayout>("bracket");
  const placeCount = Number(places);
  const outcome = useMemo(
    () =>
      computeDivision(
        dividend,
        divisor,
        mode,
        placeCount,
        strings.remainderShort
      ),
    [dividend, divisor, mode, placeCount, strings.remainderShort]
  );
  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          id="ld-dividend"
          label={strings.dividendLabel}
          mono
          numeric
          onChange={setDividend}
          value={dividend}
        />
        <TextField
          id="ld-divisor"
          label={strings.divisorLabel}
          mono
          numeric
          onChange={setDivisor}
          value={divisor}
        />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <PillGroup
          label={strings.modeLabel}
          name="ld-mode"
          onChange={setMode}
          options={[
            { value: "remainder", label: strings.modeRemainder },
            { value: "decimal", label: strings.modeDecimal },
          ]}
          value={mode}
        />
        <PillGroup
          label={strings.layoutLabel}
          name="ld-layout"
          onChange={setLayout}
          options={[
            { value: "bracket", label: strings.layoutBracket },
            { value: "european", label: strings.layoutInline },
          ]}
          value={layout}
        />
      </div>
      {mode === "decimal" ? (
        <div className="max-w-xs">
          <SelectField
            id="ld-places"
            label={strings.placesLabel}
            onChange={setPlaces}
            options={PLACE_CHOICES.map((n) => ({
              value: String(n),
              label: tPlural(strings.places, locale, n).trim(),
            }))}
            value={places}
          />
        </div>
      ) : null}
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        <Outcome
          layout={layout}
          locale={locale}
          outcome={outcome}
          places={placeCount}
          strings={strings}
        />
      </section>
    </>
  );
}

function Problem({
  kind,
  strings,
}: {
  kind: Exclude<DivisionComputation["kind"], "ok">;
  strings: Strings;
}) {
  switch (kind) {
    case "empty":
      return <Notice>{strings.enterBoth}</Notice>;
    case "invalid":
      return <ErrorNotice>{strings.invalid}</ErrorNotice>;
    case "divide-by-zero":
      return <ErrorNotice>{strings.divideByZero}</ErrorNotice>;
    default:
      return <ErrorNotice>{strings.tooLong}</ErrorNotice>;
  }
}

function Outcome({
  outcome,
  layout,
  strings,
  places,
  locale,
}: {
  outcome: DivisionComputation;
  layout: DivisionLayout;
  strings: Strings;
  places: number;
  locale: string;
}) {
  if (outcome.kind !== "ok") {
    return <Problem kind={outcome.kind} strings={strings} />;
  }
  const { view } = outcome;
  return (
    <>
      {view.division.fellBackToDecimal ? (
        <Notice>{strings.fellBack}</Notice>
      ) : null}
      {view.division.mode === "remainder" ? (
        <RemainderStats strings={strings} view={view} />
      ) : (
        <DecimalStats
          locale={locale}
          places={places}
          strings={strings}
          view={view}
        />
      )}
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copy}
        small
        text={t(strings.copyText, {
          dividend: view.dividendText,
          divisor: view.divisorText,
          answer: view.answerText,
        })}
      />
      <Working layout={layout} locale={locale} strings={strings} view={view} />
    </>
  );
}
